<template>
    <nav
        :aria-label="$t('topNav.Top')"
        class="pendo-top-nav"
        :class="{
            impersonating: isImpersonating,
            'agent-mode': isAgentModeActive,
            'sales-trial-expired': isOnTrialExpiredRoute,
            'is-blurred': isBlurred,
        }"
        data-cy="top-nav">
        <div class="pendo-top-nav__nav-content">
            <expand-collapse-nav-button
                v-if="!isAgentModeActive"
                is-top-nav />
            <pendo-button
                v-if="isImpersonating"
                v-pendo-tooltip="{
                    content: $t('topNav.Unimpersonate'),
                    arrow: true,
                }"
                class="top-nav__trigger top-nav__button-trigger"
                type="tertiary"
                size="small"
                :aria-label="$t('topNav.Unimpersonate')"
                @click="unimpersonate"
                @mouseenter="handleMouseEnterUnimpersonate"
                @mouseleave="handleMouseLeaveUnimpersonate">
                <pendo-icon
                    type="user-x"
                    size="20"
                    stroke-width="2"
                    :fill="hoveringUnimpersonate ? 'var(--palette-gray-90)' : 'var(--palette-gray-0)'" />
            </pendo-button>

            <div
                v-if="!isAgentModeActive"
                class="pendo-top-nav__left-options-container">
                <pendo-button
                    v-pendo-tooltip="{
                        content: $t('topNav.Back to previous page'),
                        arrow: true,
                    }"
                    class="pendo-top-nav__back-button"
                    pendo-analytics="back-button-nav-item"
                    type="tertiary"
                    size="small"
                    prefix-icon="arrow-left"
                    icon-size="16"
                    :disabled="endOfHistory || isOnTrialExpiredRoute"
                    :aria-label="$t('topNav.Back to previous page')"
                    @click="handleBack" />
                <!-- Used to render actions to the left of the top nav -->
                <div class="pendo-top-nav__left-options" />
            </div>
            <template v-if="isAgentModeActive">
                <img
                    v-if="!isOnAgentModeConversationPage"
                    src="@img/pendo-chevron.svg"
                    alt="Pendo chevron"
                    width="21"
                    height="21"
                    class="header-logo">
                <pendo-button
                    v-else
                    v-pendo-tooltip="{
                        content: agentModeConversationBackLabel,
                        arrow: true,
                    }"
                    class="pendo-top-nav__back-button"
                    pendo-analytics="back-button-nav-item"
                    type="tertiary"
                    size="small"
                    prefix-icon="arrow-left"
                    icon-size="16"
                    :disabled="isOnTrialExpiredRoute"
                    :aria-label="agentModeConversationBackLabel"
                    @click="handleAgentModeConversationBack" />
            </template>
            <div class="pendo-top-nav__right-options">
                <pendo-router-link
                    v-if="hasAgentMode && askLeoButtonRoute"
                    :to="askLeoButtonRoute"
                    @click="setRouteBeforeAskLeo">
                    <pendo-button
                        theme="app"
                        type="secondary"
                        size="small"
                        :class="['pendo-top-nav__ask-leo-button', { 'pendo-top-nav__ask-leo-button--on': isOnLeoPage }]"
                        @mouseenter="isLeoButtonHovered = true"
                        @mouseleave="isLeoButtonHovered = false">
                        <leo-sparkle-icon
                            circle-hidden
                            :spinning="isLeoButtonHovered" />
                        <span>{{ $t('askPendo.Ask Leo') }}</span>
                    </pendo-button>
                </pendo-router-link>

                <div
                    v-if="isAdoptEndUser && trainingAttributes?.productName"
                    class="pendo-top-nav__oem-product-name">
                    <oem-product-name />
                    <pendo-divider
                        direction="vertical"
                        decorative
                        stroke="var(--palette-gray-40)"
                        width="8px"
                        height="20px" />
                </div>
                <top-nav-right-options-wrapper :is-impersonating="isImpersonating" />
            </div>
        </div>
        <div
            v-if="isImpersonating"
            class="pendo-top-nav__candy-cane-bar" />
    </nav>
</template>

<script>
import { PendoButton, PendoIcon, PendoTooltip, PendoDivider } from '@pendo/components';
import OemProductName from '@/common/oem-product-name/oem-product-name.vue';
import ExpandCollapseNavButton from '@/common/expand-collapse-nav-button/expand-collapse-nav-button.vue';
import TopNavRightOptionsWrapper from '@/top-nav/components/top-nav-right-options-wrapper/top-nav-right-options-wrapper.vue';
import LeoSparkleIcon from '@/ask-pendo/components/leo-sparkle-icon.vue';
import PendoRouterLink from '@/vue-router/pendo-router-link.vue';

import { mapGetters, mapState } from 'vuex';
import { hasAgentModeAccess } from '@/vue-router/route-guards.utils';

export default {
    name: 'TopNav',
    components: {
        PendoButton,
        PendoDivider,
        PendoIcon,
        OemProductName,
        ExpandCollapseNavButton,
        TopNavRightOptionsWrapper,
        LeoSparkleIcon,
        PendoRouterLink,
    },
    directives: {
        PendoTooltip,
    },
    props: {
        isImpersonating: {
            type: Boolean,
            default: false,
        },
        homeSubscription: {
            type: Object,
            required: true,
        },
    },
    emits: ['toggle'],
    data () {
        return {
            hoveringUnimpersonate: false,
            endOfHistory: false,
            isLeoButtonHovered: false,
            routeBeforeAskLeo: undefined,
        };
    },
    computed: {
        ...mapState({
            isBlurred: (state) => state.leftNav.isBlurred,
        }),
        ...mapGetters({
            isAdoptEndUser: 'subscription/isAdoptEndUser',
            trainingAttributes: 'subscription/trainingAttributes',
        }),
        isAgentModeActive () {
            const route = this.$route.name;

            return this.hasAgentMode && (route === 'Leo' || route === 'LeoConversation');
        },
        isOnAgentModeConversationPage () {
            const route = this.$route?.name;

            return route === 'LeoConversation';
        },
        agentModeConversationBackLabel () {
            return this.endOfHistory
                ? this.$t('topNav.Back to Leo')
                : this.$t('topNav.Back to previous page');
        },
        hasAgentMode () {
            return hasAgentModeAccess();
        },
        isOnLeoPage () {
            const leoRoutes = ['Leo', 'LeoConversation', 'Homepage'];

            return leoRoutes.includes(this.$route?.name);
        },
        isOnTrialExpiredRoute () {
            return this.$route?.name === 'TrialExpired';
        },
        askLeoButtonRoute () {
            if (this.isOnLeoPage && this.routeBeforeAskLeo) {
                return this.routeBeforeAskLeo;
            }
            if (!this.isOnLeoPage) {
                return this.$router.pendo.toPath({ name: 'Homepage' });
            }

            return;
        },
    },
    watch: {
        $route: {
            handler () {
                this.checkHistoryState();
                this.resetRouteBeforeAskLeo();
            },
            deep: true,
            immediate: true,
        },
    },
    methods: {
        async unimpersonate () {
            await this.$router.pendo.switchSubscription(this.homeSubscription.id);
        },
        handleMouseEnterUnimpersonate () {
            this.hoveringUnimpersonate = true;
        },
        handleMouseLeaveUnimpersonate () {
            this.hoveringUnimpersonate = false;
        },
        handleBack () {
            if (!this.endOfHistory) {
                this.$router.back();
            }
        },
        handleAgentModeConversationBack () {
            if (!this.endOfHistory) {
                this.$router.back();

                return;
            }

            const target = { name: 'Homepage', query: { tab: 'askLeo' } };

            this.$router.push(this.$router.pendo.toPath(target));
        },
        checkHistoryState () {
            this.endOfHistory = !this.$router?.options?.history?.state?.back;
        },
        setRouteBeforeAskLeo () {
            const { name, params, query } = this.$route;
            this.routeBeforeAskLeo = { name, params, query };
        },
        resetRouteBeforeAskLeo () {
            if (!this.isOnLeoPage && this.routeBeforeAskLeo) {
                this.routeBeforeAskLeo = undefined;
            }
        },
    },
};
</script>

<!-- eslint-disable internal-rules/no-hardcoded-colors -->
<style scoped lang="less">
@import '@/side-nav/nav.style.less';

.pendo-top-nav {
    position: relative;
    width: 100%;
    min-height: 52px;
    max-height: 52px;
    z-index: 499;
    display: flex;
    flex-direction: column;
    background-color: var(--surface-default-background);
    border-bottom: 1px solid var(--surface-default-border);
    overflow: hidden;

     &.sales-trial-expired {
            .agent-mode-toggle {
                cursor: not-allowed;
                opacity: 0.7;

                * {
                    pointer-events: none;
                }
            }
    }

    &__nav-content {
        display: flex;
        flex-grow: 1;
        align-items: center;
        padding: 10px 16px;
        gap: 6px;
        height: 100%;

        #expand-collapse-nav-button {
            flex-shrink: 0;
            display: none;

            :deep(svg) {
                width: 18px;
                height: 18px;
            }
        }

        .screen-size(@zero, @large, {
            padding: 10px;

            #expand-collapse-nav-button {
                display: flex;
            }
        });
    }

    &__left-options-container {
        display: flex;
        align-items: center;
        gap: 16px;
        flex-shrink: 0;
    }

    &__left-options {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        gap: 10px;
        flex-shrink: 0;

        .screen-size(@zero, @medium, {
            display: none;
        });

        :deep(.pendo-button + .pendo-button) {
            margin: 0;
        }
    }

    &__back-button {
        width: 32px;
        height: 32px;
        padding: 4px;
        border-radius: 5px;
        border: 1px solid var(--palette-gray-30);
        flex-shrink: 0;

        &:focus-visible {
            outline: 2px solid var(--text-body-default-base);
            outline-offset: -2px;
        }

        :deep(.icon-prefix) {
            padding: 0;
        }

        :deep(.pendo-icon) {
            transition: transform 200ms linear;
        }

        &.is-disabled {
            border: 1px solid var(--palette-gray-30);

            &:hover {
                border: 1px solid var(--palette-gray-30);
            }
        }

        &:hover:not([aria-disabled='true']) {
            background-color: var(--surface-default-hover);

            :deep(.pendo-icon) {
                transform: translateX(-2px);
            }
        }
    }

    &__ask-leo-button {
        padding: 0 8px 0 2px;
        border: 1px solid var(--palette-gray-40);
        color: var(--text-body-default-base);

        &:hover {
            border-color: var(--palette-gray-60);
            color: var(--text-body-default-base);
        }

        &--on {
            border-color: transparent;
            background: linear-gradient(112.5deg, var(--palette-gray-0) 15%, var(--palette-purple-20) 185%) padding-box,
                linear-gradient(270deg, var(--palette-purple-20) 0%, var(--palette-blue-20) 50.48%, var(--palette-teal-20) 100%) border-box;
        }
    }

    &__oem-product-name {
        display: flex;
        align-items: center;
        gap: 6px;
    }

    &__right-options {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        flex-grow: 1;
        gap: 6px;
        margin-left: 10px;

        .v-popover {
            height: 32px;

            &:last-of-type {
                margin-left: 4px;
            }
        }
    }

    &.impersonating {
        .pendo-top-nav__nav-content {
            padding: 7px 10px 6px 10px;
        }
    }

    &.agent-mode {
        border-bottom: none;
    }

    &__candy-cane-bar {
        height: 6px;
        width: 100%;
        background: linear-gradient(
            90deg,
            var(--palette-pink-100) 0%,
            var(--brand-neutrals-brand-darkle) 34.62%,
            var(--palette-pink-100) 67.31%,
            var(--brand-neutrals-brand-darkle) 100%
        );
    }

    .agent-mode-toggle {
        margin-right: 6px;
        display: flex;
        flex-shrink: 0;
        gap: 8px;
        align-items: center;
        justify-content: center;

        :deep(.pendo-toggle__label) {
            color: var(--text-body-default-base);
            font-weight: 600;
        }

        &.is-checked {
            :deep(.pendo-toggle__slide) {
                background-color: var(--palette-teal-90);
            }
        }
    }

    .pendo-button + .pendo-button {
        margin: 0;
    }
}
</style>

<style lang="less">
.top-nav__popover {
    border-radius: 4px;
    z-index: 20001; /* needs to go above side-panels, which some are set at 20000 to go over the RC */
    overflow-y: auto;
    max-height: clamp(120px, 55vh, 400px);

    .pendo-popover__arrow {
        display: none !important;
    }

    .pendo-popover__inner {
        padding: 0;
    }

    .nav-option {
        padding: 6px 8px;
        margin-left: 12px;
        margin-right: 12px;
        display: flex;
        align-items: center;
        gap: 4px;

        &:focus-visible {
            outline: 2px solid var(--text-body-default-base);
            outline-offset: -2px;
        }

        &:hover:not(.is-open, .selected) {
            background-color: var(--surface-default-hover);

            .filled-icon {
                opacity: 1;
            }

            .empty-icon {
                opacity: 0;
            }
        }

        &.selected {
            font-weight: 600;
            background-color: var(--surface-secondary-active);

            .filled-icon {
                opacity: 1;
            }

            .empty-icon {
                opacity: 0;
            }
        }

        &.is-open {
            background-color: var(--surface-default-hover);

            .filled-icon {
                opacity: 1;
            }

            .empty-icon {
                opacity: 0;
            }
        }
    }

    .nav-option__text {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .nav-option__tag {
        margin-left: auto;
    }
}

.pendo-top-nav__left-option {
    &:focus-visible {
        outline: 2px solid var(--text-body-default-base);
        border-radius: 5px;
        outline-offset: 1px;
    }

    .pendo-multiselect__trigger--data-measure::after {
        border-bottom: none;
    }

    &:hover .pendo-multiselect__trigger:not(.is-open, .is-disabled) {
        text-decoration: underline;
    }

    .resource-center-guide-details-header-breadcrumb,
    .announcement-module-guide-details-header-breadcrumb {
        &:focus-visible {
            outline: 2px solid var(--text-body-default-base);
            border-radius: 5px;
            outline-offset: 2px;
        }

        &:focus {
            text-decoration: none;
        }

        &:hover {
            text-decoration: underline;
        }
    }

    .pendo-multiselect__trigger--data-measure,
    .pendo-multiselect__trigger {
        background: var(--surface-default-background);
    }

    .pendo-multiselect__input {
        background: transparent;

        &-wrapper input {
            max-width: 110px;
        }
    }
}

a.pendo-top-nav__left-option {
    &:focus {
        text-decoration: none;
    }

    &:hover {
        text-decoration: underline;
    }
}
</style>
